<template>
  <div class="pt-1">
    <div class="card mb-1">
      <div class="card-body d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('Dashboard') }}</h5>
          <span class="small">
            {{ $t('Dashboard Data Calculation Info') }}
          </span>
        </div>
        <div class="d-flex align-items-center">
          <b-form-group class="mb-0 mr-1">
            <b-form-select
              v-model="selectedFilter"
              :options="filterOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </b-form-group>
          <b-form-group class="mb-0" v-if="shipmentDirectionOptions.length > 0">
            <b-form-select
              v-model="shipmentDirection"
              :options="shipmentDirectionOptions"
              size="sm"
              class="filter-select"
            ></b-form-select>
          </b-form-group>
        </div>
      </div>
    </div>
    <b-tabs size="lg" horizontal active-nav-item-class="active" @input="onTabChange">
      <b-tab v-for="tab in tabs" :key="tab.name" :active="currentTab === tab.name">
        <template #title>
          <feather-icon :icon="tab.icon" size="18" class="mr-50" />
          <span>{{ tab.title }}</span>
        </template>

        <div v-if="currentTab === tab.name">
          <h5>{{ tab.heading }}</h5>
          <p>{{ tab.description }}</p>
        </div>

        <component
          :selectedFilter="selectedFilter"
          :shipmentDirection="shipmentDirection"
          :is="tab.component"
          v-if="currentTab === tab.name"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardText,
  BTabs,
  BTab,
  BDropdown,
  BDropdownItem,
  BFormSelect,
  BFormGroup
} from 'bootstrap-vue';
import { checkHasRole } from '@core/utils/roleAccess';
import useService from '@/auth/service/useService';
import { ROLES } from '@/constants/roles';
import GeneralDashboard from './tabs/GeneralDashboard/GeneralDashboard.vue';
import EconomicPerformance from './tabs/EconomicPerformance/EconomicPerformance.vue';
import ProviderPerformance from './tabs/ProviderPerformance/ProviderPerformance.vue';

export default {
  name: 'Dashboard',
  components: {
    BCard,
    BCardHeader,
    BCardText,
    BTabs,
    BTab,
    BFormSelect,
    BFormGroup,
    BDropdown,
    BDropdownItem,
    GeneralDashboard,
    EconomicPerformance,
    ProviderPerformance
  },
  data() {
    return {
      currentTab: 'general',
      selectedFilter: 'thisMonth',
      shipmentDirection: null,
      shipmentDirectionOptions: []
    };
  },
  computed: {
    tabs() {
      return [
        {
          name: 'general',
          title: this.$t('Business Overview'),
          icon: 'HomeIcon',
          heading: this.$t('Business Overview'),
          description: this.$t(
            'See your business performance at a glance - key metrics, trends, and highlighted statistics'
          ),
          component: GeneralDashboard
        },
        ...(checkHasRole([ROLES.ADMIN, ROLES.SUPERADMIN])
          ? [
              {
                name: 'economic-performance',
                title: this.$t('Financial Analytics'),
                icon: 'DollarSignIcon',
                heading: this.$t('Financial Analytics'),
                description: this.$t(
                  "Evaluate your business's financial health with revenue streams, profitability ratios, and growth trends"
                ),
                component: EconomicPerformance
              },
              {
                name: 'provider-performance',
                title: this.$t('Carrier Insights'),
                icon: 'TruckIcon',
                heading: this.$t('Carrier Insights'),
                description: this.$t(
                  'Detailed analysis of your logistics partners including delivery times, costs, and volume metrics'
                ),
                component: ProviderPerformance
              }
            ]
          : [])
      ];
    },
    filterOptions() {
      return [
        { value: 'today', text: this.$t('Today') },
        { value: 'yesterday', text: this.$t('Yesterday') },
        { value: 'thisWeek', text: this.$t('This Week') },
        { value: 'lastWeek', text: this.$t('Last Week') },
        { value: 'thisMonth', text: this.$t('This Month') },
        { value: 'lastMonth', text: this.$t('Last Month') },
        { value: 'thisYear', text: this.$t('This Year') },
        { value: 'lastYear', text: this.$t('Last Year') },
        { value: 'allTime', text: this.$t('All Time') }
      ];
    }
  },
  created() {
    this.setTabFromRoute();
    this.loadShipmentDirections();
  },
  watch: {
    '$route.params.tab': {
      handler() {
        this.setTabFromRoute();
      },
      immediate: true
    }
  },
  methods: {
    setTabFromRoute() {
      const tab = this.$route.params.tab || 'general';
      const validTabs = this.tabs.map(t => t.name);
      this.currentTab = validTabs.includes(tab) ? tab : 'general';
    },

    onTabChange(tabIndex) {
      const newTab = this.tabs[tabIndex].name;
      if (newTab !== this.currentTab) {
        this.$router.push({ name: 'dashboard', params: { tab: newTab } });
      }
    },

    applyFilter(filter) {
      if (!filter) return;
      this.selectedFilter = filter;
    },

    async loadShipmentDirections() {
      try {
        const res = await useService.constantService.getDashboardFilterTransportDirection();
        const options = (res?.data || []).map(item => ({ value: item.value, text: item.info }));
        this.shipmentDirectionOptions = options;
        if (options.length > 0) {
          this.shipmentDirection = options[0].value;
        }
      } catch (e) {
        this.shipmentDirectionOptions = [];
        this.shipmentDirection = null;
      }
    }
  }
};
</script>

<style scoped>
.card-title {
  margin-bottom: 0.5rem;
}

::v-deep .nav-pills {
  background-color: #f8f8f8;
  border-right: 1px solid #eee;
  height: 100%;
  min-height: 500px;
}

::v-deep .nav-pills .nav-link {
  border-radius: 0;
  padding: 0.75rem 1.25rem;
  color: #6e6b7b;
  transition: all 0.15s ease;
}

::v-deep .nav-pills .nav-link.active {
  background-color: #7367f0;
  color: white;
  box-shadow: 0 4px 18px -4px rgba(115, 103, 240, 0.65);
}

::v-deep .tab-content {
  min-height: 500px;
}
</style>
